import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import ProcessFnfWorkspace from "./ProcessFnfWorkspace";
import PayrollActionEmployeePanel from "./PayrollActionEmployeePanel";
import { PAYROLL_ACTION_ICONS } from "./payrollActionChrome";
import { ModuleActionsFrame } from "../shared/ModuleActionsFrame";

/**
 * Hub action key → RBAC screen under Payroll → Actions.
 * `run` = permission needed to Process/Open (F&F uses edit).
 */
export const PAYROLL_ACTION_RBAC = {
  assigned_structure: { screen: "pay_act_assigned_structure", run: "edit" },
  salary_revision: { screen: "pay_act_salary_revision", run: "edit" },
  update_statutory: { screen: "pay_act_update_statutory", run: "edit" },
  tax_planning: { screen: "pay_act_tax_planning", run: "edit" },
  loan: { screen: "pay_act_loan", run: "create" },
  leave_encashment: { screen: "pay_act_leave_encashment", run: "create" },
  advance: { screen: "pay_act_advance", run: "create" },
  reimbursement: { screen: "pay_act_reimbursement", run: "create" },
  process_fnf: { screen: "pay_act_process_fnf", run: "edit" },
};

const ACTION_DEFS = [
  {
    key: "assigned_structure",
    label: "Assigned Structure",
    blurb: "View or edit the salary structure assigned to an employee.",
    handler: "onAssignedStructure",
    openLabel: "Structure",
  },
  {
    key: "salary_revision",
    label: "Salary Revision",
    blurb: "Create or review salary revision, effective month and component changes.",
    handler: "onSalaryRevision",
    openLabel: "Revise",
  },
  {
    key: "update_statutory",
    label: "Update Statutory",
    blurb: "Update PF, ESI, PT, NPS, VPF, Meal and related applicability flags.",
    handler: "onUpdateStatutory",
    openLabel: "Update",
  },
  {
    key: "tax_planning",
    label: "Tax Planning",
    blurb: "Open tax planning and investment declaration for an employee.",
    handler: "onTaxPlanning",
    openLabel: "Open",
  },
  {
    key: "loan",
    label: "Loan",
    blurb: "Apply, sanction, EMI schedule and recovery for an employee.",
    handler: "onLoan",
    openLabel: "Loan",
  },
  {
    key: "leave_encashment",
    label: "Leave Encashment",
    blurb: "Request or process leave encashment per leave encashment policy.",
    handler: "onLeaveEncashment",
    openLabel: "Encash",
  },
  {
    key: "advance",
    label: "Advance",
    blurb: "Create or manage salary advances for an employee.",
    handler: "onAdvance",
    openLabel: "Advance",
  },
  {
    key: "reimbursement",
    label: "Reimbursement",
    blurb: "Open reimbursement claims and processing for an employee.",
    handler: "onReimbursement",
    openLabel: "Claims",
  },
  {
    key: "process_fnf",
    label: "Process F&F",
    blurb: "Full & Final for relieved employees — unpaid months, settlement heads, F&F List.",
    isFnf: true,
  },
];

function currentFinancialYear(payMonth = "") {
  const ym = String(payMonth || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(ym)) {
    const y = Number(ym.slice(0, 4));
    const m = Number(ym.slice(5, 7));
    return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
  }
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

/**
 * Payroll Actions — left action sidebar + body.
 * Visibility / Process gated per Payroll → Actions screen.
 *
 * @param {(screenKey: string, action?: string) => boolean} canActionScreen
 */
export default function PayrollActionsHub({
  darkMode = false,
  payMonth = "",
  canActionScreen = () => false,
  onAssignedStructure = null,
  onSalaryRevision = null,
  onUpdateStatutory = null,
  onTaxPlanning = null,
  onLoan = null,
  onLeaveEncashment = null,
  onAdvance = null,
  onReimbursement = null,
}) {
  const handlers = {
    onAssignedStructure,
    onSalaryRevision,
    onUpdateStatutory,
    onTaxPlanning,
    onLoan,
    onLeaveEncashment,
    onAdvance,
    onReimbursement,
  };

  const visibleActions = useMemo(() => {
    return ACTION_DEFS.filter((a) => {
      const rbac = PAYROLL_ACTION_RBAC[a.key];
      if (!rbac || !canActionScreen(rbac.screen, "view")) return false;
      if (a.isFnf) return true;
      return typeof handlers[a.handler] === "function";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canActionScreen,
    onAssignedStructure,
    onSalaryRevision,
    onUpdateStatutory,
    onTaxPlanning,
    onLoan,
    onLeaveEncashment,
    onAdvance,
    onReimbursement,
  ]);

  const [activeKey, setActiveKey] = useState(() => visibleActions[0]?.key || "process_fnf");

  const active = useMemo(
    () => visibleActions.find((a) => a.key === activeKey) || visibleActions[0] || null,
    [visibleActions, activeKey]
  );

  useEffect(() => {
    if (!visibleActions.length) return;
    if (!visibleActions.some((a) => a.key === activeKey)) {
      setActiveKey(visibleActions[0].key);
    }
  }, [visibleActions, activeKey]);

  const fy = useMemo(() => currentFinancialYear(payMonth), [payMonth]);
  const activeRbac = active ? PAYROLL_ACTION_RBAC[active.key] : null;
  const canRunActive =
    !!activeRbac && canActionScreen(activeRbac.screen, activeRbac.run || "view");

  if (!visibleActions.length) {
    return (
      <p className="mt-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
        No payroll actions available for your role.
      </p>
    );
  }

  return (
    <ModuleActionsFrame
      actions={visibleActions}
      activeKey={active?.key}
      onSelect={setActiveKey}
      icons={PAYROLL_ACTION_ICONS}
    >
      {active?.isFnf ? (
        canRunActive ? (
          <ProcessFnfWorkspace
            darkMode={darkMode}
            payMonth={payMonth}
            embedded
            hideBack
            onClose={() => {}}
          />
        ) : (
          <p className="mt-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
            You can see Process F&amp;F but do not have permission to process it.
          </p>
        )
      ) : active ? (
        <PayrollActionEmployeePanel
          key={active.key}
          darkMode={darkMode}
          actionKey={active.key}
          title={active.label}
          blurb={active.blurb}
          payMonth={payMonth}
          financialYear={fy}
          openLabel={active.openLabel || "Open"}
          onOpenEmployee={(emp) => {
            if (!canRunActive) {
              toast.error(
                `You can see ${active.label} but do not have permission to process it.`
              );
              return;
            }
            const fn = handlers[active.handler];
            if (typeof fn === "function") fn(emp);
          }}
        />
      ) : null}
    </ModuleActionsFrame>
  );
}
